<script setup>
// Sales presentation of inventory — a Tower/Floor picker over the same
// units the admin Inventory tab manages (GET /nexceo/projects/:id/inventory).
// Only units explicitly marked visibility: 'customer_shareable' by the admin
// are ever shown here — admin pricing/status edits remain the only place
// units are created, priced, or have their visibility changed.
//
// Layout matches docs/addon/e.inventory1.png: left tower/floor picker,
// legend + floor plan + right-hand Floor Overview card, units table below.
// Status colors are the exact hex values from Settings > NEXCEO Projects >
// Inventory > status (Nexceo::UnitInventory::STATUSES / ProjectInventory.vue
// STATUS_OPTIONS) — the single canonical source, not re-guessed here.
//
// The floor plan itself overlays each unit as a color-coded rectangle on
// the admin-uploaded whole-floor image for that tower/floor
// (Nexceo::FloorPlan + UnitInventory#marker — see ProjectFloorPlans.vue,
// the admin tool that creates this data). Falls back to a grid when no
// floor plan image/marker exists yet for the selected tower+floor — never
// fabricates a layout. The reference's orientation captions ("NORTH SIDE -
// GARDEN VIEW" etc.) and compass are omitted for the same reason: nothing
// in the schema records which way a building actually faces, so labeling
// a direction would be inventing a fact, not decoration.
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import unitInventoryAPI from 'dashboard/api/nexceo/unitInventory';
import floorPlansAPI from 'dashboard/api/nexceo/floorPlans';
import UnitDetailPanel from './UnitDetailPanel.vue';

const props = defineProps({
  project: { type: Object, required: true },
  projectId: { type: Number, required: true },
});

const units = ref([]);
const floorPlans = ref([]);
const isLoading = ref(false);
const error = ref(null);
const selectedTower = ref(null);
const selectedFloor = ref(null);
const selectedUnit = ref(null);
const searchTerm = ref('');
const statusFilter = ref('');

// Canonical status colors — must match ProjectInventory.vue's
// STATUS_OPTIONS exactly (that admin page is the single source of truth).
const STATUS_META = {
  available: { label: 'Available', hex: '#10b981' },
  hold: { label: 'On Hold', hex: '#f59e0b' },
  blocked: { label: 'Blocked', hex: '#f97316' },
  sold: { label: 'Sold', hex: '#ef4444' },
  unavailable: { label: 'Unavailable', hex: '#94a3b8' },
  landowner: { label: 'Landowner', hex: '#8b5cf6' },
};
const DEFAULT_STATUS_META = { label: 'Unknown', hex: '#64748b' };
function statusMeta(unit) {
  return STATUS_META[unit.status] ?? DEFAULT_STATUS_META;
}

function floorLabel(floor) {
  if (floor === 0) return 'Ground';
  const suffixes = { 1: 'st', 2: 'nd', 3: 'rd' };
  const suffix = suffixes[floor % 10] && floor % 100 < 11 ? suffixes[floor % 10] : 'th';
  return `${floor}${suffix} Floor`;
}

const customerUnits = computed(() =>
  units.value.filter(u => u.visibility === 'customer_shareable')
);

const towers = computed(() =>
  [...new Set(customerUnits.value.map(u => u.tower).filter(Boolean))].sort()
);

const unitsInTower = computed(() =>
  selectedTower.value
    ? customerUnits.value.filter(u => u.tower === selectedTower.value)
    : customerUnits.value
);

const floors = computed(() =>
  [
    ...new Set(
      unitsInTower.value
        .map(u => u.floor)
        .filter(f => f !== null && f !== undefined)
    ),
  ].sort((a, b) => a - b)
);

// Left-to-right reading order matching the physical floor plan: units the
// admin has positioned sort by their marker's horizontal (x%) position;
// unpositioned units fall back to unit_no so they still sort predictably.
const unitsInFloor = computed(() =>
  selectedFloor.value !== null
    ? [...unitsInTower.value.filter(u => u.floor === selectedFloor.value)].sort(
        (a, b) => {
          if (a.marker && b.marker) return a.marker.x - b.marker.x;
          if (a.marker) return -1;
          if (b.marker) return 1;
          return String(a.unit_no).localeCompare(String(b.unit_no));
        }
      )
    : []
);

const floorSummary = computed(() => {
  const list = unitsInFloor.value;
  return {
    total: list.length,
    available: list.filter(u => u.status === 'available').length,
    blocked: list.filter(u => u.status === 'blocked').length,
    sold: list.filter(u => u.status === 'sold').length,
  };
});

const visibleUnits = computed(() =>
  unitsInFloor.value.filter(u => {
    if (statusFilter.value && u.status !== statusFilter.value) return false;
    if (searchTerm.value.trim()) {
      return u.unit_no
        ?.toLowerCase()
        .includes(searchTerm.value.trim().toLowerCase());
    }
    return true;
  })
);

const currentFloorPlan = computed(() =>
  selectedFloor.value === null
    ? null
    : floorPlans.value.find(
        fp =>
          fp.tower === (selectedTower.value || null) &&
          fp.floor === selectedFloor.value &&
          fp.image_url
      ) || null
);
const markedVisibleUnits = computed(() =>
  visibleUnits.value.filter(u => u.marker)
);
const showFloorPlanView = computed(() => !!currentFloorPlan.value);

function markerStyle(unit) {
  const m = unit.marker;
  return {
    left: `${m.x}%`,
    top: `${m.y}%`,
    width: `${m.width}%`,
    height: `${m.height}%`,
    backgroundColor: `${statusMeta(unit).hex}33`,
    borderColor: statusMeta(unit).hex,
  };
}

function selectTower(tower) {
  selectedTower.value = tower;
  selectedFloor.value = null;
}

// ── Floor plan zoom / fullscreen (docs/addon/e.inventory1.png control
// cluster) — same requestFullscreen/fullscreenchange pattern as
// MapsTab.vue's toolbar fullscreen button, so Escape-to-exit stays in sync.
const zoomScale = ref(1);
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
function zoomIn() {
  zoomScale.value = Math.min(MAX_ZOOM, Math.round((zoomScale.value + 0.25) * 100) / 100);
}
function zoomOut() {
  zoomScale.value = Math.max(MIN_ZOOM, Math.round((zoomScale.value - 0.25) * 100) / 100);
}
const floorPlanContainerRef = ref(null);
const isFullscreen = ref(false);
function onFullscreenChange() {
  isFullscreen.value = document.fullscreenElement === floorPlanContainerRef.value;
}
function toggleFullscreen() {
  if (document.fullscreenElement) {
    document.exitFullscreen();
  } else {
    floorPlanContainerRef.value?.requestFullscreen();
  }
}
watch(selectedFloor, () => {
  zoomScale.value = 1;
});

// Natural aspect ratio of the currently-shown floor plan image, captured on
// load so the viewer box can match it exactly (no letterboxing) instead of
// distorting the image or leaving percentage-based unit markers misaligned.
const imgAspect = ref(null);
function onFloorPlanImageLoad(e) {
  imgAspect.value = e.target.naturalWidth / e.target.naturalHeight;
}
watch(currentFloorPlan, () => {
  imgAspect.value = null;
});

// Auto-select the first floor whenever the available set changes (tower
// switch, initial load) — the reference design always has a floor picked,
// with no "All Floors" combined view.
watch(floors, list => {
  if (!list.includes(selectedFloor.value)) {
    selectedFloor.value = list.length ? list[0] : null;
  }
});

function formatPrice(unit) {
  if (unit.price_display) return unit.price_display;
  const v = Number(unit.price);
  if (!v) return null;
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
  return `₹${v.toLocaleString()}`;
}

function exportCsv() {
  const cols = [
    'unit_no',
    'tower',
    'floor',
    'bhk',
    'unit_type',
    'carpet_area',
    'balcony_area',
    'super_builtup_area',
    'facing',
    'status',
    'price_display',
  ];
  const header = [
    'Unit No',
    'Tower',
    'Floor',
    'BHK',
    'Type',
    'Carpet Area',
    'Balcony Area',
    'SBA',
    'Facing',
    'Status',
    'Price',
  ];
  const rows = visibleUnits.value.map(u =>
    cols.map(c => `"${(u[c] ?? '').toString().replace(/"/g, '""')}"`).join(',')
  );
  const csv = [header.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${props.project?.name || 'inventory'}-units.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

async function load() {
  isLoading.value = true;
  error.value = null;
  try {
    const [{ data: unitData }, { data: floorPlanData }] = await Promise.all([
      unitInventoryAPI.list(props.projectId),
      floorPlansAPI.list(props.projectId),
    ]);
    units.value = Array.isArray(unitData) ? unitData : [];
    floorPlans.value = Array.isArray(floorPlanData) ? floorPlanData : [];
    if (towers.value.length) selectedTower.value = towers.value[0];
  } catch {
    error.value = 'Failed to load inventory.';
  } finally {
    isLoading.value = false;
  }
}

onMounted(() => {
  load();
  document.addEventListener('fullscreenchange', onFullscreenChange);
});
onBeforeUnmount(() => {
  document.removeEventListener('fullscreenchange', onFullscreenChange);
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="flex-1 overflow-y-auto p-6">
    <div v-if="isLoading" class="flex justify-center py-16">
      <span class="i-lucide-loader-2 size-5 animate-spin text-n-slate-7" />
    </div>

    <div
      v-else-if="error"
      class="text-sm text-red-600 dark:text-red-400 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30"
    >
      {{ error }}
    </div>

    <div v-else-if="!customerUnits.length" class="text-center py-16">
      <span
        class="i-lucide-layout-grid size-8 text-n-slate-6 mx-auto block mb-2"
      />
      <p class="text-sm font-medium text-n-slate-9">
        No units available to present yet.
      </p>
    </div>

    <div v-else class="flex flex-col gap-5">
     <div class="flex gap-5 items-start">
      <!-- Left: Tower + Floor picker -->
      <div class="w-52 shrink-0 flex flex-col gap-3">
        <div class="relative">
          <select
            v-model="selectedTower"
            class="reset-base w-full appearance-none text-sm font-semibold px-3 py-2 !mb-0 rounded-lg !border !border-solid border-n-weak bg-n-background text-n-slate-12 outline-none focus:ring-2 focus:ring-woot-500"
            @change="selectTower(selectedTower)"
          >
            <option v-for="tower in towers" :key="tower" :value="tower">
              Tower {{ tower }}
            </option>
          </select>
          <span
            class="i-lucide-chevron-down size-4 text-n-slate-9 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
          />
        </div>
        <div class="flex flex-col gap-1.5 max-h-[70vh] overflow-y-auto">
          <button
            v-for="floor in floors"
            :key="floor"
            type="button"
            class="text-left px-3 py-2 rounded-lg text-sm font-medium !border !border-solid transition-colors"
            :class="
              selectedFloor === floor
                ? '!border-woot-500 bg-woot-50 text-woot-700 dark:bg-woot-500/10 dark:text-woot-400'
                : '!border-slate-200 dark:!border-n-slate-5 text-n-slate-11 hover:bg-n-alpha-2 hover:!border-slate-300'
            "
            @click="selectedFloor = floor"
          >
            {{ floorLabel(floor) }}
          </button>
        </div>
      </div>

      <!-- Right: everything else -->
      <div class="flex-1 min-w-0 flex flex-col gap-5">
        <!-- Legend -->
        <div class="flex items-center justify-end gap-4 flex-wrap text-xs font-medium text-n-slate-11">
          <span
            v-for="key in ['available', 'sold', 'blocked', 'hold', 'landowner']"
            :key="key"
            class="flex items-center gap-1.5"
          >
            <span
              class="size-2.5 rounded-full"
              :style="{ backgroundColor: STATUS_META[key].hex }"
            />
            {{ STATUS_META[key].label }}
          </span>
        </div>

        <div class="grid grid-cols-1 xl:grid-cols-[1fr_260px] gap-5 items-start">
          <!-- Floor Plan -->
          <div class="flex flex-col gap-2">
            <div
              v-if="showFloorPlanView"
              ref="floorPlanContainerRef"
              class="relative rounded-xl overflow-auto border border-n-weak bg-n-background flex items-center justify-center h-[min(58vh,420px)] md:h-[min(62vh,480px)] xl:h-[min(66vh,560px)] 2xl:h-[min(70vh,640px)]"
              :class="isFullscreen ? '!h-screen' : ''"
            >
              <div
                class="relative shrink-0 transition-transform duration-150"
                :style="{
                  height: '100%',
                  aspectRatio: imgAspect || 4 / 3,
                  transform: `scale(${zoomScale})`,
                }"
              >
                <img
                  :src="currentFloorPlan.image_url"
                  alt="Floor plan"
                  class="w-full h-full block"
                  @load="onFloorPlanImageLoad"
                />
                <button
                  v-for="unit in markedVisibleUnits"
                  :key="unit.id"
                  type="button"
                  class="absolute !border-2 !border-solid flex items-center justify-center text-[10px] font-semibold text-n-slate-12 hover:brightness-95 transition-all"
                  :style="markerStyle(unit)"
                  :title="`${unit.unit_no} — ${statusMeta(unit).label}`"
                  @click="selectedUnit = unit"
                >
                  {{ unit.unit_no }}
                </button>
                <p
                  v-if="!markedVisibleUnits.length"
                  class="absolute inset-x-0 bottom-0 p-2 text-[11px] text-white bg-black/50"
                >
                  No units have been positioned on this floor plan yet.
                </p>
              </div>

              <!-- Zoom / fullscreen controls (docs/addon/e.inventory1.png) -->
              <div
                class="absolute bottom-3 right-3 flex flex-col gap-1.5 z-10"
              >
                <button
                  type="button"
                  class="flex items-center justify-center size-8 rounded-lg bg-n-background !border !border-solid border-n-weak shadow-sm text-n-slate-11 hover:bg-n-alpha-2 transition-colors disabled:opacity-40"
                  :disabled="zoomScale >= MAX_ZOOM"
                  title="Zoom in"
                  @click="zoomIn"
                >
                  <span class="i-lucide-plus size-4" />
                </button>
                <button
                  type="button"
                  class="flex items-center justify-center size-8 rounded-lg bg-n-background !border !border-solid border-n-weak shadow-sm text-n-slate-11 hover:bg-n-alpha-2 transition-colors disabled:opacity-40"
                  :disabled="zoomScale <= MIN_ZOOM"
                  title="Zoom out"
                  @click="zoomOut"
                >
                  <span class="i-lucide-minus size-4" />
                </button>
                <button
                  type="button"
                  class="flex items-center justify-center size-8 rounded-lg bg-n-background !border !border-solid border-n-weak shadow-sm text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
                  title="Fullscreen"
                  @click="toggleFullscreen"
                >
                  <span class="i-lucide-maximize size-4" />
                </button>
              </div>
            </div>

            <!-- Fallback: no floor plan image yet for this tower+floor -->
            <div
              v-else
              class="grid gap-3 grid-cols-[repeat(auto-fill,minmax(200px,1fr))]"
            >
              <button
                v-for="unit in visibleUnits"
                :key="unit.id"
                type="button"
                class="flex flex-col gap-2 text-left p-4 rounded-xl !border !border-solid border-n-weak hover:!border-woot-300 hover:shadow-md transition-all"
                @click="selectedUnit = unit"
              >
                <div class="flex items-start justify-between gap-2">
                  <span class="text-sm font-semibold text-n-slate-12">{{
                    unit.unit_no
                  }}</span>
                  <span
                    class="text-xs font-medium px-2 py-0.5 rounded-full shrink-0"
                    :style="{
                      backgroundColor: `${statusMeta(unit).hex}22`,
                      color: statusMeta(unit).hex,
                    }"
                  >
                    {{ statusMeta(unit).label }}
                  </span>
                </div>
                <p class="text-xs text-n-slate-10">
                  {{
                    [unit.bhk, unit.unit_type].filter(Boolean).join(' · ') ||
                    '—'
                  }}
                </p>
                <p
                  v-if="formatPrice(unit)"
                  class="text-sm font-semibold text-woot-600 dark:text-woot-400 mt-auto"
                >
                  {{ formatPrice(unit) }}
                </p>
              </button>
              <p v-if="!visibleUnits.length" class="text-xs text-n-slate-9 col-span-full">
                No units on this floor yet.
              </p>
            </div>
          </div>

          <!-- Floor Overview -->
          <div class="flex flex-col gap-3">
            <div class="rounded-xl border border-n-weak p-4">
              <h2 class="text-sm font-semibold text-n-slate-12 mb-3">
                Floor Overview
              </h2>
              <div class="grid grid-cols-4 xl:grid-cols-2 gap-3">
                <div>
                  <p class="text-lg font-bold text-n-slate-12">
                    {{ floorSummary.total }}
                  </p>
                  <p class="text-[11px] font-medium text-n-slate-9">Total Units</p>
                </div>
                <div>
                  <p
                    class="text-lg font-bold"
                    :style="{ color: STATUS_META.available.hex }"
                  >
                    {{ floorSummary.available }}
                  </p>
                  <p class="text-[11px] font-medium text-n-slate-9">Available</p>
                </div>
                <div>
                  <p
                    class="text-lg font-bold"
                    :style="{ color: STATUS_META.blocked.hex }"
                  >
                    {{ floorSummary.blocked }}
                  </p>
                  <p class="text-[11px] font-medium text-n-slate-9">Blocked</p>
                </div>
                <div>
                  <p
                    class="text-lg font-bold"
                    :style="{ color: STATUS_META.sold.hex }"
                  >
                    {{ floorSummary.sold }}
                  </p>
                  <p class="text-[11px] font-medium text-n-slate-9">Sold</p>
                </div>
              </div>
            </div>
            <div
              class="flex items-start gap-2 rounded-xl bg-woot-50 dark:bg-woot-500/10 p-3"
            >
              <span class="i-lucide-info size-4 text-woot-600 dark:text-woot-400 shrink-0 mt-0.5" />
              <div>
                <p class="text-xs font-semibold text-n-slate-12">
                  Click on any unit to view details
                </p>
                <p class="text-[11px] text-n-slate-9 mt-0.5">
                  Explore floor plans, pricing and availability.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
     </div>

      <!-- Detailed table (full width — not constrained to the sidebar's column, so it doesn't leave a tall blank gap once the short floor list ends) -->
      <div class="flex flex-col gap-3">
          <div class="flex items-center justify-between gap-3 flex-wrap">
            <h2 class="text-sm font-semibold text-n-slate-12">
              Units{{
                selectedFloor !== null ? ` on ${floorLabel(selectedFloor)}` : ''
              }}
              <span class="text-n-slate-9 font-normal"
                >({{ visibleUnits.length }})</span
              >
            </h2>
            <div class="flex items-center gap-2">
              <div class="relative">
                <span
                  class="i-lucide-search absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-n-slate-7 pointer-events-none"
                />
                <input
                  v-model="searchTerm"
                  type="text"
                  placeholder="Search unit no…"
                  class="reset-base pl-8 pr-3 py-1.5 !mb-0 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:ring-2 focus:ring-woot-500 w-48"
                />
              </div>
              <div class="relative">
                <span
                  class="i-lucide-filter absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-n-slate-7 pointer-events-none"
                />
                <select
                  v-model="statusFilter"
                  class="appearance-none pl-8 pr-6 py-1.5 text-xs font-medium rounded-lg !border !border-solid border-n-weak bg-n-background text-n-slate-11 outline-none focus:ring-2 focus:ring-woot-500"
                >
                  <option value="">All statuses</option>
                  <option
                    v-for="(meta, key) in STATUS_META"
                    :key="key"
                    :value="key"
                  >
                    {{ meta.label }}
                  </option>
                </select>
              </div>
              <button
                type="button"
                class="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg !border !border-solid border-n-weak text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
                @click="exportCsv"
              >
                <span class="i-lucide-download size-3.5" />
                Export
              </button>
            </div>
          </div>

          <div class="overflow-x-auto rounded-xl border border-n-weak">
            <table class="w-full text-sm">
              <thead>
                <tr
                  class="border-b border-n-weak bg-n-alpha-1 text-left text-xs font-semibold text-n-slate-10"
                >
                  <th class="px-3 py-2 font-semibold">Unit No.</th>
                  <th class="px-3 py-2 font-semibold">Type</th>
                  <th class="px-3 py-2 font-semibold">Carpet Area</th>
                  <th class="px-3 py-2 font-semibold">Balcony Area</th>
                  <th class="px-3 py-2 font-semibold">Saleable Area</th>
                  <th class="px-3 py-2 font-semibold">Facing</th>
                  <th class="px-3 py-2 font-semibold">Status</th>
                  <th class="px-3 py-2 font-semibold">Price</th>
                  <th class="px-3 py-2 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="unit in visibleUnits"
                  :key="unit.id"
                  class="border-b border-n-weak last:border-0 hover:bg-n-alpha-1 transition-colors"
                >
                  <td class="px-3 py-2 font-medium text-n-slate-12">
                    {{ unit.unit_no }}
                  </td>
                  <td class="px-3 py-2 text-n-slate-11">
                    {{ unit.bhk || '—' }}
                  </td>
                  <td class="px-3 py-2 text-n-slate-11">
                    {{
                      unit.carpet_area
                        ? `${Number(unit.carpet_area).toLocaleString()} sq ft`
                        : '—'
                    }}
                  </td>
                  <td class="px-3 py-2 text-n-slate-11">
                    {{
                      unit.balcony_area
                        ? `${Number(unit.balcony_area).toLocaleString()} sq ft`
                        : '—'
                    }}
                  </td>
                  <td class="px-3 py-2 text-n-slate-11">
                    {{
                      unit.super_builtup_area
                        ? `${Number(unit.super_builtup_area).toLocaleString()} sq ft`
                        : '—'
                    }}
                  </td>
                  <td class="px-3 py-2 text-n-slate-11">
                    {{ unit.facing || '—' }}
                  </td>
                  <td class="px-3 py-2">
                    <span
                      class="text-xs font-medium px-2 py-0.5 rounded-full"
                      :style="{
                        backgroundColor: `${statusMeta(unit).hex}22`,
                        color: statusMeta(unit).hex,
                      }"
                    >
                      {{ statusMeta(unit).label }}
                    </span>
                  </td>
                  <td class="px-3 py-2 font-medium text-n-slate-12">
                    {{ formatPrice(unit) || '—' }}
                  </td>
                  <td class="px-3 py-2 text-right">
                    <button
                      type="button"
                      class="text-xs font-medium text-woot-600 dark:text-woot-400 hover:underline"
                      @click="selectedUnit = unit"
                    >
                      View
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
    </div>

    <UnitDetailPanel
      v-if="selectedUnit"
      :unit="selectedUnit"
      :project="project"
      @close="selectedUnit = null"
    />
  </div>
</template>
